import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  GeraeteArtefakte,
  GeraeteAudit,
  Parametersatz,
  ParametersatzRequest,
  Parameterstand,
  Zugangsdaten,
  ZugangsdatenKlartext,
  ZugangsdatenRequest
} from '../../../../../Models/geraete-artefakte.model';
import { GeraeteArtefaktService } from '../../../../../Services/geraete-artefakt.service';

/** Formularzustand für eine Parameterdatei – anlegen und bearbeiten teilen ihn. */
interface ParametersatzForm extends ParametersatzRequest {
  /** null = neu anlegen, sonst die Id des bearbeiteten Satzes. */
  id: number | null;

  /**
   * Beim NEUANLEGEN Pflicht.
   * Beim Bearbeiten eines bestehenden Parametersatzes nicht nötig.
   */
  datei: File | null;

  /** Optionaler Kommentar zum ersten Parameterstand. */
  kommentar: string;
}

/** Formularzustand für Zugangsdaten. */
interface ZugangsdatenForm extends ZugangsdatenRequest {
  id: number | null;
}

/**
 * Parameterdateien und Zugangsdaten eines Geräts an einer Messstelle.
 *
 * Bewusst eine eigene Komponente statt eines weiteren Abschnitts in der ohnehin
 * grossen Detailseite: der Bereich hat eigene Rechte, eigene Ladezustände und
 * ein eigenes Sicherheitsprotokoll.
 *
 * Zum Umgang mit den Geheimnissen:
 * - Klartext wird nie mit der Liste geladen, sondern erst auf Klick – jeder
 *   Abruf erzeugt serverseitig einen Audit-Eintrag.
 * - Angezeigter Klartext wird beim Verlassen der Seite und beim Zuklappen
 *   wieder aus dem Speicher geworfen.
 * - Beim Bearbeiten bleibt das Passwortfeld leer; leer heisst „unverändert".
 *   Das bestehende Geheimnis muss dafür nicht angezeigt werden.
 */
@Component({
  selector: 'app-geraete-artefakte',
  templateUrl: './geraete-artefakte.component.html',
  styleUrls: ['./geraete-artefakte.component.css']
})
export class GeraeteArtefakteComponent implements OnChanges, OnDestroy {

  /** Messstelle, deren Artefakte angezeigt werden. */
  @Input() messstelleId: number | null = null;

  artefakte: GeraeteArtefakte | null = null;

  isLoading = false;
  loadError = '';

  // ── Parameterdateien ──


  /** Aufgeklappte Parameterdateien (Id-Menge). */
  offeneSaetze = new Set<number>();

  satzForm: ParametersatzForm | null = null;
  satzFehler = '';
  savingSatz = false;

  /** Datei und Kommentar je Parameterdatei, für den nächsten Stand. */
  standDatei = new Map<number, File>();
  standKommentar = new Map<number, string>();

  /** Parameterdateien, für die gerade ein Upload läuft. */
  private uploadende = new Set<number>();

  /** Stände, deren Download gerade läuft. */
  private ladendeStaende = new Set<number>();

  // ── Zugangsdaten ──

  zugangForm: ZugangsdatenForm | null = null;
  zugangFehler = '';
  savingZugang = false;

  /** Aufgedeckte Geheimnisse, nur im Speicher dieser Ansicht. */
  klartext = new Map<number, ZugangsdatenKlartext>();

  /** Zugangsdaten, deren Klartext gerade geholt wird. */
  private aufdeckende = new Set<number>();

  // ── Sicherheitsprotokoll ──

  audit: GeraeteAudit[] = [];
  auditOffen = false;
  auditLaedt = false;
  auditFehler = '';

  constructor(
    private service: GeraeteArtefaktService,
    private snackBar: MatSnackBar
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messstelleId']) {
      this.zuruecksetzen();
      this.lade();
    }
  }

  ngOnDestroy(): void {
    // Geheimnisse nicht über das Verlassen der Seite hinaus im Speicher halten.
    this.klartext.clear();
  }

  // ========================================================
  // LADEN
  // ========================================================

  private zuruecksetzen(): void {
    this.artefakte = null;
    this.loadError = '';
    this.offeneSaetze.clear();
    this.satzForm = null;
    this.zugangForm = null;
    this.klartext.clear();
    this.standDatei.clear();
    this.standKommentar.clear();
    this.audit = [];
    this.auditOffen = false;
  }

  private lade(oeffneSatzId: number | null = null): void {
    if (this.messstelleId == null) {
      return;
    }

    this.isLoading = true;
    this.loadError = '';

    this.service.getArtefakte(this.messstelleId).subscribe({

      next: artefakte => {

        this.artefakte = artefakte;
        this.isLoading = false;

        // Falls gerade eine neue Parameterdatei hochgeladen wurde,
        // direkt geöffnet anzeigen.
        if (oeffneSatzId != null) {
          this.offeneSaetze.add(oeffneSatzId);
        }
      },

      error: err => {

        this.isLoading = false;

        this.loadError = this.fehlertext(
          err,
          'Die Geräte-Artefakte konnten nicht geladen werden.'
        );
      }
    });
  }

  get canEdit(): boolean {
    return this.artefakte?.canEdit === true;
  }

  get canSeeZugangsdaten(): boolean {
    return this.artefakte?.canSeeZugangsdaten === true;
  }

  get canManageZugangsdaten(): boolean {
    return this.artefakte?.canManageZugangsdaten === true;
  }

  get parametersaetze(): Parametersatz[] {
    return this.artefakte?.parametersaetze ?? [];
  }

  get zugangsdaten(): Zugangsdaten[] {
    return this.artefakte?.zugangsdaten ?? [];
  }

  // ========================================================
  // PARAMETERDATEIEN
  // ========================================================

  istOffen(satz: Parametersatz): boolean {
    return this.offeneSaetze.has(satz.id);
  }

  toggleSatz(satz: Parametersatz): void {
    if (this.offeneSaetze.has(satz.id)) {
      this.offeneSaetze.delete(satz.id);
    } else {
      this.offeneSaetze.add(satz.id);
    }
  }

  neuerSatz(): void {
    this.satzForm = {
      id: null,
      datei: null,
      kommentar: '',

      bezeichnung: '',
      geraeteHersteller: '',
      geraeteTyp: '',
      seriennummer: '',
      beschreibung: '',
      istVertraulich: false
    };

    this.satzFehler = '';
  }

  bearbeiteSatz(satz: Parametersatz): void {
    this.satzForm = {
      id: satz.id,

      datei: null,
      kommentar: '',

      bezeichnung: satz.bezeichnung,
      geraeteHersteller: satz.geraeteHersteller ?? '',
      geraeteTyp: satz.geraeteTyp ?? '',
      seriennummer: satz.seriennummer ?? '',
      beschreibung: satz.beschreibung ?? '',
      istVertraulich: satz.istVertraulich
    };

    this.satzFehler = '';
  }

  brichSatzAb(): void {
    this.satzForm = null;
    this.satzFehler = '';
  }

  onNeueSatzDateiAusgewaehlt(event: Event): void {
    if (!this.satzForm || this.satzForm.id != null) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const datei = input.files?.[0] ?? null;

    this.satzForm.datei = datei;

    if (datei) {

      /*
       * Wenn noch keine Bezeichnung eingetragen wurde,
       * den Dateinamen ohne Erweiterung übernehmen.
       *
       * Beispiel:
       *
       * SINAMICS_G120_Pumpe.par
       * ↓
       * SINAMICS_G120_Pumpe
       */
      if (!this.satzForm.bezeichnung.trim()) {
        this.satzForm.bezeichnung =
          datei.name.replace(/\.[^.]+$/, '');
      }
    }

    // Damit auch dieselbe Datei erneut gewählt werden kann.
    input.value = '';
  }

  entferneNeueSatzDatei(): void {
    if (!this.satzForm || this.satzForm.id != null) {
      return;
    }

    this.satzForm.datei = null;
  }

  get satzIstGueltig(): boolean {
    if (!this.satzForm) {
      return false;
    }

    if (!this.satzForm.bezeichnung.trim()) {
      return false;
    }

    /*
     * Beim Anlegen muss eine echte Parameterdatei vorhanden sein.
     */
    if (
      this.satzForm.id == null &&
      this.satzForm.datei == null
    ) {
      return false;
    }

    return true;
  }

  speichereSatz(): void {
    if (
      !this.satzForm ||
      !this.satzIstGueltig ||
      this.savingSatz ||
      this.messstelleId == null
    ) {
      return;
    }

    this.savingSatz = true;
    this.satzFehler = '';

    const form = this.satzForm;

    const request: ParametersatzRequest = {
      bezeichnung: form.bezeichnung.trim(),
      geraeteHersteller: this.leerZuNull(form.geraeteHersteller),
      geraeteTyp: this.leerZuNull(form.geraeteTyp),
      seriennummer: this.leerZuNull(form.seriennummer),
      beschreibung: this.leerZuNull(form.beschreibung),
      istVertraulich: form.istVertraulich
    };


    // ======================================================
    // BESTEHENDEN PARAMETERSATZ BEARBEITEN
    // ======================================================

    if (form.id != null) {

      this.service
        .updateParametersatz(form.id, request)
        .subscribe({

          next: satz => {
            this.uebernehmeSatz(satz, false);

            this.savingSatz = false;
            this.satzForm = null;

            this.snackBar.open(
              'Parameterdatei gespeichert.',
              'OK',
              { duration: 2500 }
            );
          },

          error: err => {
            this.savingSatz = false;

            this.satzFehler = this.fehlertext(
              err,
              'Die Parameterdatei konnte nicht gespeichert werden.'
            );
          }
        });

      return;
    }


    // ======================================================
    // NEUE PARAMETERDATEI + VERSION 1 HOCHLADEN
    // ======================================================

    const datei = form.datei;

    if (!datei) {
      this.savingSatz = false;
      this.satzFehler = 'Bitte eine Parameterdatei auswählen.';
      return;
    }


    this.service
      .createParametersatz(
        this.messstelleId,

        // 2. Parameter = echte Datei
        datei,

        // 3. Parameter = Metadaten
        {
          bezeichnung: request.bezeichnung,
          geraeteHersteller: request.geraeteHersteller,
          geraeteTyp: request.geraeteTyp,
          seriennummer: request.seriennummer,
          beschreibung: request.beschreibung,
          istVertraulich: request.istVertraulich,
          kommentar: this.leerZuNull(form.kommentar)
        }
      )
      .subscribe({

        next: satz => {

          this.savingSatz = false;
          this.satzForm = null;

          // Vollständigen aktuellen Zustand vom Server holen
          // und neuen Satz direkt öffnen.
          this.lade(satz.id);

          this.snackBar.open(
            'Parameterdatei hochgeladen.',
            'OK',
            { duration: 3000 }
          );
        },

        error: err => {
          this.savingSatz = false;

          this.satzFehler = this.fehlertext(
            err,
            'Die Parameterdatei konnte nicht hochgeladen werden.'
          );
        }
      });
  }

  /** Fügt einen neuen Satz ein bzw. ersetzt den bestehenden – ohne Neuladen. */
  private uebernehmeSatz(
    satz: Parametersatz,
    istNeu: boolean
  ): void {

    if (!this.artefakte) {
      return;
    }

    if (istNeu) {

      this.artefakte.parametersaetze =
        [
          ...this.artefakte.parametersaetze,
          satz
        ]
          .sort(
            (a, b) =>
              a.bezeichnung.localeCompare(
                b.bezeichnung,
                'de'
              )
          );


      // Direkt öffnen, damit Version 1 sichtbar ist.
      this.offeneSaetze.add(
        satz.id
      );

      return;
    }


    this.artefakte.parametersaetze =
      this.artefakte.parametersaetze
        .map(
          p =>
            p.id === satz.id
              ? satz
              : p
        )
        .sort(
          (a, b) =>
            a.bezeichnung.localeCompare(
              b.bezeichnung,
              'de'
            )
        );
  }

  loescheSatz(satz: Parametersatz): void {
    const anzahl = satz.staende.length;
    const zusatz = anzahl > 0
      ? `\n\n${anzahl} gespeicherte${anzahl === 1 ? 'r Stand wird' : ' Stände werden'} mitgelöscht.`
      : '';

    if (!confirm(`Parameterdatei „${satz.bezeichnung}" wirklich löschen?${zusatz}`)) {
      return;
    }

    this.service.deleteParametersatz(satz.id).subscribe({
      next: () => {
        if (this.artefakte) {
          this.artefakte.parametersaetze =
            this.artefakte.parametersaetze.filter(p => p.id !== satz.id);
        }
        this.offeneSaetze.delete(satz.id);
        this.standDatei.delete(satz.id);
        this.standKommentar.delete(satz.id);
        this.snackBar.open('Parameterdatei gelöscht.', 'OK', { duration: 2500 });
      },
      error: err => this.melde(err, 'Die Parameterdatei konnte nicht gelöscht werden.')
    });
  }

  // ── Stände ──

  onDateiAusgewaehlt(satz: Parametersatz, event: Event): void {
    const input = event.target as HTMLInputElement;
    const datei = input.files?.[0];

    if (datei) {
      this.standDatei.set(satz.id, datei);
    }

    // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
    input.value = '';
  }

  gewaehlteDatei(satz: Parametersatz): File | null {
    return this.standDatei.get(satz.id) ?? null;
  }

  verwirfDatei(satz: Parametersatz): void {
    this.standDatei.delete(satz.id);
    this.standKommentar.delete(satz.id);
  }

  istUploadend(satz: Parametersatz): boolean {
    return this.uploadende.has(satz.id);
  }

  ladeStandHoch(satz: Parametersatz): void {
    const datei = this.standDatei.get(satz.id);

    if (!datei || this.uploadende.has(satz.id)) {
      return;
    }

    this.uploadende.add(satz.id);

    this.service.uploadStand(
      satz.id,
      datei,
      this.leerZuNull(this.standKommentar.get(satz.id))
    ).subscribe({
      next: stand => {
        // Der neue Stand ist der aktuelle; der bisherige verliert die Markierung.
        satz.staende = [
          stand,
          ...satz.staende.map(s => ({ ...s, istAktuell: false }))
        ];

        this.uploadende.delete(satz.id);
        this.standDatei.delete(satz.id);
        this.standKommentar.delete(satz.id);
        this.offeneSaetze.add(satz.id);

        this.snackBar.open(
          `Stand ${stand.versionNr} gespeichert.`,
          'OK',
          { duration: 2500 }
        );
      },
      error: err => {
        this.uploadende.delete(satz.id);
        this.melde(err, 'Die Datei konnte nicht hochgeladen werden.');
      }
    });
  }

  istStandLadend(stand: Parameterstand): boolean {
    return this.ladendeStaende.has(stand.id);
  }

  /**
   * Lädt einen Stand herunter.
   *
   * Der Umweg über den Blob ist nötig, weil ein einfacher Link den
   * Bearer-Token nicht mitschickt. Der Server protokolliert den Download,
   * bevor er die Datei ausliefert.
   */
  ladeStandHerunter(stand: Parameterstand): void {
    if (this.ladendeStaende.has(stand.id)) {
      return;
    }

    this.ladendeStaende.add(stand.id);

    this.service.downloadStand(stand.id).subscribe({
      next: blob => {
        this.ladendeStaende.delete(stand.id);

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = stand.fileName;
        link.click();

        // Der Browser braucht die URL nur bis zum Start des Downloads.
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      },
      error: err => {
        this.ladendeStaende.delete(stand.id);
        this.melde(err, 'Die Datei konnte nicht heruntergeladen werden.');
      }
    });
  }

  loescheStand(satz: Parametersatz, stand: Parameterstand): void {
    if (!confirm(`Stand ${stand.versionNr} („${stand.fileName}") wirklich löschen?`)) {
      return;
    }

    this.service.deleteStand(stand.id).subscribe({
      next: () => {
        const uebrig = satz.staende.filter(s => s.id !== stand.id);

        // „Aktuell" ist immer der Stand mit der höchsten Versionsnummer.
        const hoechste = uebrig.reduce((max, s) => Math.max(max, s.versionNr), 0);
        satz.staende = uebrig.map(s => ({ ...s, istAktuell: s.versionNr === hoechste }));

        this.snackBar.open('Stand gelöscht.', 'OK', { duration: 2500 });
      },
      error: err => this.melde(err, 'Der Stand konnte nicht gelöscht werden.')
    });
  }

  /** Dateigrösse lesbar – Parameterfiles sind meist klein. */
  groesse(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /** Erste zwölf Zeichen der Prüfsumme – mehr braucht ein Sichtvergleich nicht. */
  kurzeSumme(sha256: string): string {
    return (sha256 ?? '').substring(0, 12);
  }

  // ========================================================
  // ZUGANGSDATEN
  // ========================================================

  neueZugangsdaten(): void {
    this.zugangForm = {
      id: null,
      bezeichnung: '',
      zugangsart: '',
      benutzername: '',
      geheimnis: '',
      notiz: ''
    };
    this.zugangFehler = '';
  }

  /**
   * Bearbeiten ohne das bestehende Geheimnis anzuzeigen: das Feld bleibt leer
   * und wird nur übertragen, wenn wirklich etwas eingegeben wurde.
   */
  bearbeiteZugangsdaten(eintrag: Zugangsdaten): void {
    const bereitsAufgedeckt = this.klartext.get(eintrag.id);

    this.zugangForm = {
      id: eintrag.id,
      bezeichnung: eintrag.bezeichnung,
      zugangsart: eintrag.zugangsart ?? '',
      benutzername: bereitsAufgedeckt?.benutzername ?? '',
      geheimnis: '',
      notiz: bereitsAufgedeckt?.notiz ?? ''
    };
    this.zugangFehler = '';
  }

  brichZugangAb(): void {
    this.zugangForm = null;
    this.zugangFehler = '';
  }

  get zugangIstGueltig(): boolean {
    if (!this.zugangForm) {
      return false;
    }
    if ((this.zugangForm.bezeichnung ?? '').trim().length === 0) {
      return false;
    }
    // Beim Anlegen ist ein Geheimnis Pflicht, beim Ändern optional.
    return this.zugangForm.id != null
      || (this.zugangForm.geheimnis ?? '').length > 0;
  }

  speichereZugangsdaten(): void {
    if (!this.zugangForm || !this.zugangIstGueltig || this.savingZugang || this.messstelleId == null) {
      return;
    }

    this.savingZugang = true;
    this.zugangFehler = '';

    const form = this.zugangForm;
    const request: ZugangsdatenRequest = {
      bezeichnung: form.bezeichnung.trim(),
      zugangsart: this.leerZuNull(form.zugangsart),
      benutzername: this.leerZuNull(form.benutzername),
      // Leer heisst beim Ändern ausdrücklich „unverändert".
      geheimnis: (form.geheimnis ?? '').length > 0 ? form.geheimnis : null,
      notiz: this.leerZuNull(form.notiz)
    };

    const aufruf = form.id == null
      ? this.service.createZugangsdaten(this.messstelleId, request)
      : this.service.updateZugangsdaten(form.id, request);

    aufruf.subscribe({
      next: eintrag => {
        if (this.artefakte) {
          this.artefakte.zugangsdaten = (form.id == null
            ? [...this.artefakte.zugangsdaten, eintrag]
            : this.artefakte.zugangsdaten.map(z => (z.id === eintrag.id ? eintrag : z))
          ).sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung, 'de'));
        }

        // Ein geänderter Eintrag darf keinen veralteten Klartext behalten.
        this.klartext.delete(eintrag.id);

        this.savingZugang = false;
        this.zugangForm = null;
        this.snackBar.open(
          form.id == null ? 'Zugangsdaten gespeichert.' : 'Zugangsdaten aktualisiert.',
          'OK',
          { duration: 2500 }
        );
      },
      error: err => {
        this.savingZugang = false;
        this.zugangFehler = this.fehlertext(err, 'Die Zugangsdaten konnten nicht gespeichert werden.');
      }
    });
  }

  loescheZugangsdaten(eintrag: Zugangsdaten): void {
    if (!confirm(`Zugangsdaten „${eintrag.bezeichnung}" wirklich löschen?`)) {
      return;
    }

    this.service.deleteZugangsdaten(eintrag.id).subscribe({
      next: () => {
        if (this.artefakte) {
          this.artefakte.zugangsdaten =
            this.artefakte.zugangsdaten.filter(z => z.id !== eintrag.id);
        }
        this.klartext.delete(eintrag.id);
        this.snackBar.open('Zugangsdaten gelöscht.', 'OK', { duration: 2500 });
      },
      error: err => this.melde(err, 'Die Zugangsdaten konnten nicht gelöscht werden.')
    });
  }

  istAufgedeckt(eintrag: Zugangsdaten): boolean {
    return this.klartext.has(eintrag.id);
  }

  aufgedeckt(eintrag: Zugangsdaten): ZugangsdatenKlartext | null {
    return this.klartext.get(eintrag.id) ?? null;
  }

  istAufdeckend(eintrag: Zugangsdaten): boolean {
    return this.aufdeckende.has(eintrag.id);
  }

  /**
   * Holt den Klartext. Der Abruf ist die protokollierte Aktion – deshalb wird
   * er nicht vorsorglich beim Laden der Seite gemacht.
   */
  deckeAuf(eintrag: Zugangsdaten): void {
    if (this.aufdeckende.has(eintrag.id)) {
      return;
    }

    this.aufdeckende.add(eintrag.id);

    this.service.zeigeZugangsdaten(eintrag.id).subscribe({
      next: klartext => {
        this.aufdeckende.delete(eintrag.id);
        this.klartext.set(eintrag.id, klartext);

        eintrag.zuletztAngezeigtAmUtc = new Date().toISOString();

        if (klartext.unlesbar) {
          this.snackBar.open(
            'Das gespeicherte Geheimnis lässt sich nicht entschlüsseln. Bitte neu erfassen.',
            'OK',
            { duration: 6000 }
          );
        }
      },
      error: err => {
        this.aufdeckende.delete(eintrag.id);
        this.melde(err, 'Die Zugangsdaten konnten nicht angezeigt werden.');
      }
    });
  }

  /** Blendet den Klartext wieder aus und wirft ihn aus dem Speicher. */
  verberge(eintrag: Zugangsdaten): void {
    this.klartext.delete(eintrag.id);
  }

  verbergeAlle(): void {
    this.klartext.clear();
  }

  /** Kopiert einen Wert in die Zwischenablage, ohne ihn anzuzeigen. */
  kopiere(wert: string | null | undefined, was: string): void {
    if (!wert) {
      return;
    }

    navigator.clipboard?.writeText(wert).then(
      () => this.snackBar.open(`${was} in die Zwischenablage kopiert.`, 'OK', { duration: 2000 }),
      () => this.snackBar.open('Kopieren wurde vom Browser abgelehnt.', 'OK', { duration: 3000 })
    );
  }

  // ========================================================
  // SICHERHEITSPROTOKOLL
  // ========================================================

  toggleAudit(): void {
    this.auditOffen = !this.auditOffen;

    if (this.auditOffen && this.audit.length === 0) {
      this.ladeAudit();
    }
  }

  ladeAudit(): void {
    if (this.messstelleId == null || this.auditLaedt) {
      return;
    }

    this.auditLaedt = true;
    this.auditFehler = '';

    this.service.getAudit(this.messstelleId).subscribe({
      next: eintraege => {
        this.audit = eintraege ?? [];
        this.auditLaedt = false;
      },
      error: err => {
        this.auditLaedt = false;
        this.auditFehler = this.fehlertext(err, 'Das Sicherheitsprotokoll konnte nicht geladen werden.');
      }
    });
  }

  auditIcon(eintrag: GeraeteAudit): string {
    if (!eintrag.erfolgreich) {
      return 'block';
    }

    switch (eintrag.aktion) {
      case 'ParameterstandHochgeladen':
        return 'upload_file';
      case 'ParameterstandHeruntergeladen':
        return 'download';
      case 'ZugangsdatenAngezeigt':
        return 'visibility';
      case 'ParametersatzGeloescht':
      case 'ParameterstandGeloescht':
      case 'ZugangsdatenGeloescht':
        return 'delete_outline';
      case 'ZugangsdatenAngelegt':
      case 'ZugangsdatenGeaendert':
        return 'key';
      default:
        return 'description';
    }
  }

  // ========================================================
  // HILFEN
  // ========================================================

  trackBySatz(_index: number, satz: Parametersatz): number {
    return satz.id;
  }

  trackByStand(_index: number, stand: Parameterstand): number {
    return stand.id;
  }

  trackByZugang(_index: number, eintrag: Zugangsdaten): number {
    return eintrag.id;
  }

  trackByAudit(_index: number, eintrag: GeraeteAudit): number {
    return eintrag.id;
  }

  private leerZuNull(wert: string | null | undefined): string | null {
    const getrimmt = (wert ?? '').trim();
    return getrimmt.length > 0 ? getrimmt : null;
  }

  /**
   * Serverseitige Meldung bevorzugen: bei 403 steht dort der Grund (fehlende
   * Rolle oder fehlendes HTTPS), und genau der hilft weiter.
   */
  private fehlertext(err: any, standard: string): string {
    return err?.error?.message
      ?? (err?.status === 403
        ? 'Für diese Aktion fehlt die Berechtigung.'
        : standard);
  }

  private melde(err: any, standard: string): void {
    this.snackBar.open(this.fehlertext(err, standard), 'OK', { duration: 4000 });
  }
}
